import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fromBase64Url, linkAccessToken, unwrap, deriveSubkeys, decryptContent } from '../shared/activity-viewer/crypto/activityCrypto';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.monalerte.app';

/**
 * Invitation v2 : le secret est dans le fragment (#k=…), jamais envoyé au
 * serveur. Il ouvre l'enveloppe du lien pour afficher le lieu, puis est
 * transmis à l'application par le lien monalerte://.
 */
function readInvitationSecret() {
  const value = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('k');
  if (value) window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
  return value && /^[A-Za-z0-9_-]{43}$/.test(value) ? value : null;
}

async function revealMeeting(token, invitation, secret) {
  if (invitation.cryptoVersion !== 2 || !secret || !invitation.encryptedMeeting) return invitation;
  const linkSecret = fromBase64Url(secret);
  const response = await fetch(`${API_URL}/api/rendez-vous/invite/${encodeURIComponent(token)}/wraps/link`, {
    headers: { 'X-Activity-Access': await linkAccessToken(linkSecret) },
  });
  if (!response.ok) return invitation;
  const { rdvId, envelope } = await response.json();
  const { events } = await deriveSubkeys(await unwrap(envelope, rdvId, { linkSecret }));
  const meeting = JSON.parse(await decryptContent(events, invitation.encryptedMeeting));
  return { ...invitation, address: meeting.address };
}

export default function RendezVousInvitePage() {
  const { token = '' } = useParams();
  const [state, setState] = useState({ loading: true, invitation: null, error: '' });
  const secret = useMemo(readInvitationSecret, []);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/rendez-vous/invite/${encodeURIComponent(token)}`)
      .then(async response => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || 'Invitation introuvable ou expirée.');
        return revealMeeting(token, payload.rendezVous, secret).catch(() => payload.rendezVous);
      })
      .then(invitation => active && setState({ loading: false, invitation, error: '' }))
      .catch(error => active && setState({ loading: false, invitation: null, error: error.message }));
    return () => { active = false; };
  }, [token, secret]);

  const appLink = `monalerte://rendez-vous/${encodeURIComponent(token)}${secret ? `#k=${secret}` : ''}`;

  return (
    <main style={{ minHeight: '100vh', padding: '36px 20px 72px', background: 'radial-gradient(circle at top, #1d3357 0%, #050b14 52%)', color: 'white' }}>
      <section className="glass-card" style={{ maxWidth: 620, margin: '0 auto', padding: 'clamp(24px, 6vw, 48px)' }}>
        <Link to="/le-projet" style={{ color: 'var(--mga-gold)', fontWeight: 700 }}>← MonAlerte</Link>
        <p className="badge-gold" style={{ marginTop: 28 }}>Invitation MonAlerte</p>
        {state.loading ? <p role="status">Chargement du rendez-vous…</p> : state.error ? (
          <p role="alert" style={{ color: '#fca5a5' }}>{state.error}</p>
        ) : (
          <>
            <h1 style={{ fontSize: 'clamp(2rem, 7vw, 3rem)', marginBottom: 18 }}>{state.invitation.title}</h1>
            <p>Organisé par {state.invitation.creator?.displayName || 'un membre MonAlerte'}</p>
            <p><strong>Lieu :</strong> {(!state.invitation.address || state.invitation.address === 'Non défini' || state.invitation.address === 'Lieu à convenir ensemble') ? 'Non défini' : state.invitation.address}</p>
            <p><strong>Date & Heure :</strong> {(() => {
              const val = state.invitation.scheduledAt;
              if (!val || val === 'Non défini' || val === 'null' || val === 'undefined') return 'Non défini';
              const d = new Date(val);
              return isNaN(d.getTime()) ? 'Non défini' : d.toLocaleString('fr-FR');
            })()}</p>
            {state.invitation.isAccepted && <p>Cette invitation a déjà été acceptée.</p>}
            <a href={appLink} className="button-primary" style={{ display: 'block', textAlign: 'center', marginTop: 28, textDecoration: 'none' }}>
              Ouvrir dans MonAlerte
            </a>
            <p style={{ marginTop: 16, color: 'var(--mga-slate-300)' }}>
              L’acceptation de l’invitation nécessite l’application MonAlerte et un compte connecté.
            </p>
          </>
        )}
      </section>
    </main>
  );
}
